import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * <AutoFitTitle> — a title element that measures itself and shrinks its
 * font size until the widest word fits inside the container. Prevents
 * long unbreakable words (e.g. compound words like "Rushgoalieworth")
 * from overflowing the viewport on mobile.
 *
 * Uses a two-pass approach:
 *  1. Render at the natural font size given by className.
 *  2. On layout, if `scrollWidth > clientWidth`, iteratively multiply
 *     the font-size by `step` (default 0.94) until either it fits OR
 *     hits `minFontRatio` (default 0.55 of the natural size).
 *
 * `overflow-wrap: anywhere` is set as a final safety net so a single
 * word longer than any possible reduced size still wraps rather than
 * clipping the viewport.
 *
 * Re-measures on ResizeObserver events (orientation flip, viewport
 * resize) and when the `children` text changes.
 */
export default function AutoFitTitle({
  as: Tag = "h1",
  children,
  className = "",
  minFontRatio = 0.55,
  step = 0.94,
  ...rest
}) {
  const ref = useRef(null);
  // Scale is a multiplier applied to the natural font-size. 1 = no
  // shrink; 0.55 = smallest permitted (~half the original).
  const [scale, setScale] = useState(1);
  const [fontSize, setFontSize] = useState("");
  // Whether the element still overflows at the min font ratio — only
  // then do we fall back to breaking words at arbitrary characters
  // (`overflow-wrap: anywhere`). Otherwise we leave word-wrap alone so
  // words stay intact after shrinking.
  const [needsAnywhere, setNeedsAnywhere] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      // Measure with word-breaking DISABLED so `scrollWidth` reflects
      // the true unbroken word width. If we left `overflow-wrap:
      // anywhere` set from the first paint (previous version), the
      // browser wraps the long word before we get a chance to measure,
      // and the shrink loop never runs. Restore whichever mode is
      // needed at the end.
      el.style.overflowWrap = "normal";
      el.style.wordBreak = "normal";
      el.style.fontSize = "";
      const naturalPx = parseFloat(window.getComputedStyle(el).fontSize);
      if (!naturalPx || Number.isNaN(naturalPx)) return;

      let current = naturalPx;
      const floor = naturalPx * minFontRatio;
      // Guard: bail after 40 iterations so a pathological layout can't
      // freeze the tab.
      let guard = 0;
      while (el.scrollWidth > el.clientWidth && current > floor && guard < 40) {
        current *= step;
        el.style.fontSize = `${current}px`;
        guard += 1;
      }
      const newScale = current / naturalPx;
      setScale(newScale);
      setFontSize(newScale < 1 ? `${current}px` : "");
      // After shrinking to the floor, if the widest word STILL doesn't
      // fit, engage the last-resort character-level wrap so the title
      // wraps rather than clipping the viewport.
      const stillOverflows = el.scrollWidth > el.clientWidth;
      setNeedsAnywhere(stillOverflows);
      if (stillOverflows) {
        el.style.overflowWrap = "anywhere";
      }
    };

    fit();

    const ro = new ResizeObserver(fit);
    ro.observe(el);
    // Also observe the parent — the title's width is determined by its
    // container, so container resizes matter too.
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
    // Re-run when the rendered text changes.
  }, [children, minFontRatio, step]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        ...(fontSize ? { fontSize } : {}),
        ...(needsAnywhere ? { overflowWrap: "anywhere" } : {})
      }}
      data-fit-scale={scale.toFixed(3)}
      data-fit-anywhere={needsAnywhere ? "true" : "false"}
      {...rest}
    >
      {children}
    </Tag>
  );
}
